/**
 * Messewand-Produktseite: Galerie mit Lightbox, Reiter, Mengenfeld, Ankerleiste.
 *
 * Portiert aus dem Protex-Theme (assets/product-ui.js), gekürzt auf das, was
 * die Picura-Seite braucht. Bewusst NICHT übernommen:
 *   - <product-form>   Vision bringt ein eigenes mit (assets/product.js:711),
 *                      das den Warenkorb-Zähler und die Cart-Sections nachzieht.
 *   - <variant-picker> Alle Messewand-Artikel haben genau eine Variante.
 *   - <sticky-atc>     Dafür steht picura-sticky-cta im Template.
 *
 * Alle Elementnamen tragen das Präfix picura-mw-, weil Vision in app.js und
 * product.js unter anderem product-form und product-recommendations registriert.
 * Beide Themes benutzen customElements.get-Wächter, es wirft also nichts —
 * stattdessen gewinnt still, wer zuerst lädt, und fremdes Markup träfe auf eine
 * fremde Klasse. Das Präfix schliesst das aus.
 */
(function () {
  'use strict';

  /** Registriert und meldet sich, wenn der Name schon vergeben ist. */
  function def(name, cls) {
    if (customElements.get(name)) {
      console.warn('[picura-mw] Elementname bereits vergeben:', name);
      return;
    }
    customElements.define(name, cls);
  }

  /* ------------------------------------------------------------------ *
   * <picura-mw-qty> — Mengenfeld mit zwei Schaltflächen
   * ------------------------------------------------------------------ */
  class MwQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;

      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-step]');
        if (!button) return;

        const step = parseInt(button.dataset.step, 10) || 1;
        const min = parseInt(this.input.min, 10) || 1;
        const next = (parseInt(this.input.value, 10) || min) + step;

        this.input.value = Math.max(min, next);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  /* ------------------------------------------------------------------ *
   * <picura-mw-tabs> — ARIA-Reiter mit Pfeiltastensteuerung
   * ------------------------------------------------------------------ */
  class MwTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (!this.tabs.length) return;

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
      });

      // Der Merkmals-Button springt auf #pmw-tab-specs. Ohne das hier landete
      // er auf einem Reiter, der noch zu ist.
      this.openFromHash();
      this.onHash = () => this.openFromHash();
      window.addEventListener('hashchange', this.onHash);
    }

    disconnectedCallback() {
      if (this.onHash) window.removeEventListener('hashchange', this.onHash);
    }

    openFromHash() {
      const id = window.location.hash.slice(1);
      if (!id) return;
      const index = this.tabs.findIndex((tab) => tab.id === id);
      if (index >= 0) this.select(index);
    }

    onKeydown(event, index) {
      const keys = { ArrowRight: 1, ArrowLeft: -1 };
      if (event.key in keys) {
        event.preventDefault();
        const next = (index + keys[event.key] + this.tabs.length) % this.tabs.length;
        this.select(next);
        this.tabs[next].focus();
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.select(0);
        this.tabs[0].focus();
      } else if (event.key === 'End') {
        event.preventDefault();
        const last = this.tabs.length - 1;
        this.select(last);
        this.tabs[last].focus();
      }
    }

    select(index) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', String(active));
        // Rollender Tabindex: nur der aktive Reiter liegt in der Tabfolge.
        tab.tabIndex = active ? 0 : -1;

        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !active;
      });
    }
  }

  /* ------------------------------------------------------------------ *
   * <picura-mw-gallery> — Miniaturen, Bühne, Lightbox
   * ------------------------------------------------------------------ */
  class MwGallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.dialog = this.querySelector('[data-zoom-dialog]');
      this.index = 0;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.show(parseInt(thumb.dataset.thumb, 10)));
      });

      this.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-zoom]');
        if (trigger) this.zoom(parseInt(trigger.dataset.tile, 10));
        if (event.target.closest('[data-zoom-close]') && this.dialog) this.dialog.close();
        if (event.target.closest('[data-zoom-prev]')) this.step(-1);
        if (event.target.closest('[data-zoom-next]')) this.step(1);
      });

      // Pfeiltasten nur bei offener Lightbox — sonst gehören sie der Seite.
      this.onKey = (event) => {
        if (!this.dialog || !this.dialog.open) return;
        if (event.key === 'ArrowLeft') this.step(-1);
        if (event.key === 'ArrowRight') this.step(1);
      };
      document.addEventListener('keydown', this.onKey);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
    }

    show(index) {
      this.index = index;
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      this.thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
    }

    /** Blättert in der Lightbox und zieht die Bühne mit. */
    step(delta) {
      if (!this.slides.length) return;
      const next = (this.index + delta + this.slides.length) % this.slides.length;
      this.show(next);
      this.zoom(next, true);
    }

    zoom(index, keepOpen) {
      if (!this.dialog) return;
      const slide = this.slides[index];
      const trigger = slide && slide.querySelector('[data-zoom]');
      const body = this.dialog.querySelector('[data-zoom-body]');
      if (!trigger || !body) return;

      this.index = index;

      // Das Bild entsteht erst beim Öffnen: das Seitenverhältnis wechselt je
      // Medium, und ein leeres src wäre ein Request ins Nichts.
      const img = document.createElement('img');
      img.className = 'pmw-zoom__img';
      img.src = trigger.dataset.zoom;
      img.alt = '';
      img.decoding = 'async';

      body.replaceChildren(img);
      if (!keepOpen) this.dialog.showModal();
    }
  }

  /* ------------------------------------------------------------------ *
   * <picura-mw-anchors> — markiert den Abschnitt, den man gerade liest
   * ------------------------------------------------------------------ */
  class MwAnchors extends HTMLElement {
    connectedCallback() {
      this.links = Array.from(this.querySelectorAll('[data-anchor]'));
      const targets = this.links
        .map((link) => document.getElementById(link.dataset.anchor))
        .filter(Boolean);

      if (!targets.length) return;

      this.watchStuck();

      // Die Leiste sitzt unter dem Header, ein Ziel gilt also erst als
      // „aktuell", wenn es diese Linie überschreitet — nicht schon am
      // Fensterrand.
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.links.forEach((link) =>
              link.classList.toggle('is-active', link.dataset.anchor === entry.target.id)
            );
          });
        },
        { rootMargin: '-40% 0px -55% 0px' }
      );

      targets.forEach((target) => this.observer.observe(target));
    }

    /**
     * Ein unsichtbarer Wächter direkt über der Leiste: verlässt er den
     * Sichtbereich, klebt die Leiste und bekommt ihre Kante. Billiger und
     * ruckelfreier als ein Scroll-Listener.
     *
     * Protex misst hier zusätzlich einen schrumpfenden Sticky-Header nach.
     * Picuras Header ist nicht sticky und setzt --header-height ohnehin schon
     * selbst (picura-header.liquid), deshalb entfällt das.
     */
    watchStuck() {
      const sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      this.parentNode.insertBefore(sentinel, this);

      this.stuckObserver = new IntersectionObserver(
        ([entry]) => this.classList.toggle('is-stuck', !entry.isIntersecting),
        { threshold: 1 }
      );
      this.stuckObserver.observe(sentinel);
      this.sentinel = sentinel;
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.stuckObserver) this.stuckObserver.disconnect();
      if (this.sentinel) this.sentinel.remove();
    }
  }

  def('picura-mw-qty', MwQuantity);
  def('picura-mw-tabs', MwTabs);
  def('picura-mw-gallery', MwGallery);
  def('picura-mw-anchors', MwAnchors);
})();
